/**
 * Компонент-обёртка для фильтров, применяемых в заголовках виджетов и других частях интерфейса.
 *
 * Реализует общие стили: `display: flex`, `gap`, `hover color`, `cursor: pointer`
 * Позволяет передать любые props `div`, включая `className`, `style`, `onClick` и др.
 *
 * @example
 * ```tsx
 * <FilterContainer>
 *   <StatusFilter />
 *   <DateRangeFilter />
 *   <TagFilter />
 * </FilterContainer>
 * ```
 *
 * @param {React.ReactNode} children - Дочерние элементы, например, фильтры.
 * @param {string} [className] - Дополнительные CSS-классы, объединяются с базовыми стилями.
 * @param {React.HTMLAttributes<HTMLDivElement>} rest - Остальные HTML-пропсы передаются на div.
 *
 * @returns {JSX.Element} Обёртка для фильтров с унифицированным стилем.
 */

import React, { MutableRefObject } from 'react';

import cn from 'classnames';

import cls from './FilterContainer.module.css';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    innerRef?: MutableRefObject<HTMLDivElement>;
    isHovered?: boolean;
    isDisabled?: boolean;
}

export const FilterContainer = ({
    children,
    className = '',
    innerRef,
    isHovered = false,
    isDisabled = false,
    ...rest
}: Props) => (
    <div
        className={cn(
            cls.container,
            className,
            isHovered && cls.isHovered,
            isDisabled && cls.disabled
        )}
        {...rest}
        ref={innerRef ?? null}
    >
        {children}
    </div>
);
